<template>
  <div>
    <div class="container p-5">
      <div v-if="product" class="row g-5">
        <!-- Left Column -->
        <div class="col-12 col-sm-5 col-lg-3">
          <div class="product-image-container">
            <img
              :src="
                selectedCover ?
                `${BASE_IMAGE_PREFIX}/${selectedCover}` :
                `${PLACEHOLDER_IMAGE}`
              "
              alt="Product image"
              class="img-fluid product-image"
            />
          </div>
          <div class="d-flex gap-2">
            <img
              v-for="(image, index) in product.images"
              :key="`mini-${index}`"
              :src="
                image ?
                `${BASE_IMAGE_PREFIX}/${image}` :
                `${PLACEHOLDER_IMAGE}`
              "
              alt="Product image"
              class="img-fluid mini-view"
              @click="selectedCover = image"
            />
          </div>
        </div>

        <!-- Right Column -->
        <div class="col-sm-7 col-lg-9">
          <!-- Stock Status -->
          <div class="stock-status mb-2">
            <span v-if="product.stock > 0" class="status-badge me-2 mb-2 mb-md-0">In Stock</span>
            <span v-if="product.productCategory === 'stationary'" class="status-badge">Quality Stationary Item</span>
          </div>

          <!-- Title -->
          <h1 class="product-title">{{ product.title }}</h1>

          <!-- Meta Information -->
          <div
            v-if="product.brand"
            class="product-meta"
          >
            <span class="meta-label">Brand:</span>
            <router-link
              :to="'/brand/' + product.brand.replace(/ /g, '_')"
              style="text-decoration: none"
            >
              <span class="meta-value">
                {{ product.brand }}
              </span>
            </router-link>
          </div>

          <!-- Price -->
          <div class="price-container" v-if="selectedVariant">
            <span class="price">
              <span v-if="selectedVariant.isOnSale && selectedVariant.salePrice">
                <span class="text-decoration-line-through text-muted">
                  ৳ {{ selectedVariant.price }}
                </span>
                <span> ৳ {{ selectedVariant.salePrice }}</span>
              </span>
              <span v-else>
                ৳ {{ selectedVariant.price }}
              </span>
            </span>
          </div>

          <div
            v-if="product.variants?.length"
            class="d-flex gap-2 my-3 flex-wrap"
          >
            <button
              v-for="variant in product.variants"
              :key="variant.name"
              class="btn custom-btn"
              :class="selectedVariant?.name === variant.name ? null : 'outline'"
              @click="selectedVariant = variant"
            >
              {{ variant.name }}
            </button>
          </div>
          <div class="counter-wrapper">
            <button
              class="btn-counter"
              @click="quantity--"
              :disabled="quantity <= 1"
            >
              -
            </button>
            <span class="">{{ quantity }}</span>
            <button class="btn-counter" @click="quantity++">+</button>
          </div>

          <!-- Action Buttons -->
          <div class="action-buttons">
            <button class="btn btn-add-cart" @click="addToCart(product)">
              <i class="bi bi-cart3"></i>
              Add to cart
            </button>
            <button class="btn btn-wishlist" @click="buyNow(product)">
              Buy now
            </button>
            <button class="btn btn-wishlist" @click="addToWishlist(product)">
              <i class="bi bi-heart"></i>
              Add to wishlist
            </button>
            <button class="btn btn-wishlist" @click="addToCompare(product)">
              <i class="bi bi-arrow-left-right"></i>
              Compare
            </button>
          </div>

          <!-- Description -->
          <div class="description">
            <p>{{ product.description }}</p>
          </div>

          <!-- Skincare Details -->
          <div class="product-attributes">
            <div v-if="product.ingredients" class="attribute-row">
              <span class="meta-label">Ingredients:</span>
              <span class="attribute-value">{{ product.ingredients }}</span>
            </div>
            <div v-if="product.skinTypes?.length" class="attribute-row">
              <span class="meta-label">Skin Types:</span>
              <span class="attribute-value">{{ product.skinTypes.join(', ') }}</span>
            </div>
            <div v-if="product.concerns?.length" class="attribute-row">
              <span class="meta-label">Concerns:</span>
              <span class="attribute-value">{{ product.concerns.join(', ') }}</span>
            </div>
            <div v-if="product.countryOfOrigin" class="attribute-row">
              <span class="meta-label">Country of Origin:</span>
              <span class="attribute-value">{{ product.countryOfOrigin }}</span>
            </div>
            <div v-if="product.warning" class="attribute-row">
              <span class="meta-label">Warning:</span>
              <span class="attribute-value">{{ product.warning }}</span>
            </div>
          </div>

          <!-- Categories -->
          <div class="categories-section">
            <div class="category-row">
              <span class="meta-label">Categories:</span>
              <div
                class="category-link"
                v-for="(category, index) in product.category"
                :key="`category-${index}`"
              >
                <router-link
                  :to="'/search/?c=' + category.toLowerCase().replace(/ /g, '_')"
                  style="text-decoration: none"
                >
                  <span class="category-text">
                    {{ category.replace(/_/g, ' ') }}
                  </span>
                </router-link>
              </div>
            </div>
          </div>

          <!-- Social Share -->
          <div class="social-share">
            <a
              href="https://www.facebook.com/sehrishmart55/"
              target="_blank"
              class="social-icon custom"
            >
              <i class="bi bi-facebook"></i>
            </a>
            <a
              href="https://www.instagram.com/sehrish_mart.official/"
              target="_blank"
              class="social-icon custom"
            >
              <i class="bi bi-instagram"></i>
            </a>
            <a
              href="https://wa.me/8801882124009"
              target="_blank"
              class="social-icon custom"
            >
              <i class="bi bi-whatsapp"></i>
            </a>
          </div>
        </div>
      </div>
    </div>
    <!-- Recent Posts Section -->
    <div class="recent-posts mt-5 container-fluid container-sm">
      <h3>More Products</h3>
      <hr />
      <div>
        <!-- Product Section -->
        <div v-if="recentProducts.length" class="product-section">
          <div class="row g-4">
            <!-- Product Card -->
            <div
              v-for="(product, i) in recentProducts"
              :key="`product-${i}`"
              class="col-6 col-sm-4 col-lg-2"
            >
              <div class="product-card">
                <img
                  role="button"
                  @click="fetchProductDetails(product._id)"
                  :src="
                    product.images.length ?
                    `${BASE_IMAGE_PREFIX}/${product.images?.[0]}` :
                    `${PLACEHOLDER_IMAGE}`
                  "
                  alt="Product image"
                  class="product-image"
                  draggable="false"
                />
                <h5
                  role="button"
                  @click="fetchProductDetails(product._id)"
                  class="product-title-mini"
                >
                  {{ product.title }}
                </h5>
                <div
                  v-if="product.brand"
                  class="product-brand"
                >
                  {{ product.brand }}
                </div>
                <div class="product-price" v-if="product.minVariant">
                  <span v-if="product.minVariant.salePrice">
                    <span class="text-muted text-decoration-line-through">
                      ৳ {{ product.minVariant.price }}
                    </span>
                    ৳ {{ product.minVariant.salePrice }}
                  </span>
                  <span v-else>
                    ৳ {{ product.minVariant.price }}
                  </span>
                </div>
                <div
                  class="card-footer p-0 pb-4 pt-0 border-top-0 bg-transparent"
                >
                  <div class="text-center">
                    <span
                      class="btn btn-outline-dark btn-sm mt-auto me-1 text-uppercase"
                      style="font-size: 10px"
                      @click="addToCart(product)"
                    >
                      <i class="bi bi-cart3"></i> Add to cart
                    </span>
                    <span
                      class="btn btn-outline-dark btn-sm mt-auto me-1 text-uppercase"
                      style="font-size: 10px"
                      @click="addToWishlist(product)"
                    >
                      <i class="bi bi-suit-heart"></i>
                    </span>
                    <span
                      class="btn btn-outline-dark btn-sm mt-auto text-uppercase"
                      style="font-size: 10px"
                      @click="addToCompare(product)"
                    >
                      <i class="bi bi-arrow-left-right"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="text-center mt-5">
          <p>No products available.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import {
  BASE_URL,
  BASE_IMAGE_PREFIX,
  PLACEHOLDER_IMAGE,
} from "@/globals";
import { analytics } from '@/services/analytics';

export default {
  name: "SingleProduct",
  data() {
    return {
      BASE_IMAGE_PREFIX,
      PLACEHOLDER_IMAGE,
      product: null,
      quantity: 1,
      recentProducts: [],
      selectedCover: "",
      // isHardCover: false,
      // selectedCoverType: "paperback",
      selectedVariant: null
    };
  },
  mounted() {
    this.fetchProductDetails();
    this.fetchRecentProducts();
  },
  computed: {
    pageMeta() {
      return {
        title: this.product?.title || "Sehrish mart",
        description: "Place for quality skincare items",
        image: this.product?.images?.[0] || "",
      };
    },
  },
  methods: {
    async fetchProductDetails(id) {
      const productId = id || this.$route.params.id;
      const slug = this.$route.params.slug;
      try {
        const response = await axios.get(`${BASE_URL}/api/products/${productId}`);
        this.product = response.data;
        // default to first variant
        if (this.product.variants?.length) {
          this.selectedVariant = this.product.variants[0];
        }
        this.updatePageMeta();
        const correctSlug = this.slugify(this.product.title);
        if (!slug || slug !== correctSlug) {
          this.$router.replace({
            name: 'ProductDetails',
            params: { id: productId, slug: correctSlug }
          });
        }
        this.selectedCover = this.product.images.length ? this.product.images[0] : "";
        this.scrollToTop();
        analytics.viewContent(this.product, this.selectedVariant.price)
      } catch (error) {
        console.error("Error fetching product details:", error);
      }
    },
    async fetchRecentProducts() {
      try {
        const response = await axios.get(`${BASE_URL}/api/products?limit=6&hasActiveVariant=true,`);
        this.recentProducts = response.data.products;
      } catch (error) {
        console.error("Error fetching product details:", error);
      }
    },
    slugify(text) {
      return text
        .toString()                  // convert to string
        .normalize("NFD")            // normalize accents (café → cafe)
        .replace(/[\u0300-\u036f]/g, "") // remove diacritics
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, "") // remove invalid chars
        .replace(/\s+/g, "-")         // collapse whitespace and replace by -
        .replace(/-+/g, "-");         // collapse dashes
    },
    variantFor(product) {
      // Main product uses the on-page selected variant; recent-product cards use their own default.
      return product._id === this.product?._id && this.selectedVariant
        ? this.selectedVariant
        : product.minVariant;
    },
    addToCart(product) {
      const variant = this.variantFor(product);
      const cartItem = {
        product: product,
        quantity: this.quantity,
        variant
      };

      this.$store.dispatch("addToCart", cartItem);

      this.$root.showToast({
        message: "Item added to cart",
        status: "success",
        duration: 3000,
      });
      analytics.addToCart(product,
        variant.salePrice || variant.price,
        this.quantity)
    },
    addToWishlist(product) {
      const variant = this.variantFor(product);
      const wishedItem = {
        ...product,
        quantity: this.quantity,
        variant
      };
      this.$store.dispatch("addToWishlist", wishedItem);
      this.$root.showToast({
        message: "Item added to wishlist",
        status: "success",
        duration: 3000,
      });
      analytics.addToWishlist(product,
        variant.salePrice || variant.price,
        this.quantity)
    },
    addToCompare(product) {
      const compareItems = this.$store.getters.compareItems;
      if (compareItems.includes(product._id)) {
        this.$root.showToast({
          message: "Product is already in compare list",
          status: "error",
          duration: 3000,
        });
        return;
      }
      if (compareItems.length >= 2) {
        this.$root.showToast({
          message: "Maximum 2 items can be compared at a time",
          status: "error",
          duration: 3000,
        });
        return;
      }
      this.$store.dispatch("addToCompare", product._id);
      this.$root.showToast({
        message: "Item added to compare",
        status: "success",
        duration: 3000,
      });
    },
    buyNow(product) {
      const cartItem = {
        product: product,
        quantity: this.quantity,
        variant: this.variantFor(product)
      };
      this.$store.dispatch("addToCart", cartItem);
      this.$router.push("/checkout");
    },
    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    },
    updatePageMeta() {
      const { title, description, image } = this.pageMeta;

      document.title = title;

      const setMeta = (attribute, name, content) => {
        let meta = document.head.querySelector(
          `meta[${attribute}="${name}"]`
        );

        if (!meta) {
          meta = document.createElement("meta");
          meta.setAttribute(attribute, name);
          document.head.appendChild(meta);
        }

        meta.setAttribute("content", content || "");
      };

      setMeta("name", "description", description);
      setMeta("property", "og:title", title);
      setMeta("property", "og:description", description);

      if (image) {
        setMeta("property", "og:image", image);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.stock-status {
  .status-badge {
    display: inline-block;
    padding: 6px 12px;
    background-color: #ecf7ed;
    color: #198754;
    font-size: 14px;
    font-weight: 500;
    border-radius: 4px;
  }
}

.product-title {
  font-size: 42px;
  font-weight: 700;
  color: #111827;
  margin: 10px 0;
}

.product-meta {
  display: flex;
  align-items: center;
  gap: 24px;
  margin-bottom: 10px;

  .meta-label {
    color: #6b7280;
    font-size: 15px;
  }

  .meta-value {
    color: #111827;
    font-weight: 500;
  }

  .rating-container {
    display: flex;
    align-items: center;
    gap: 8px;

    .stars {
      color: #ffa41c;
      font-size: 14px;
    }

    .rating-number {
      color: #6b7280;
      font-size: 14px;
    }
  }

  .sku-container {
    font-size: 14px;
  }
}

.price-container {
  // margin: 32px 0;

  .price {
    font-size: 36px;
    font-weight: 600;
    color: #e11d48;
  }
}

.description {
  color: #6b7280;
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 32px;
  white-space: pre-line;
}

.custom-switch {
  cursor: pointer;
}
.custom-switch:checked {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
}
.custom-switch:focus {
  box-shadow: 0 0 0 0.25rem rgba(25, 135, 84, 0.25);
}

.silent-info {
  font-size: 10px;
  color: var(--text-gray);
}

.action-buttons {
  display: flex;
  gap: 15px;
  margin-bottom: 40px;

  .btn {
    padding: 10px 20px;
    font-size: 16px;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 8px;
    border-radius: 6px;

    i {
      font-size: 20px;
    }
  }

  .btn-add-cart {
    background-color: var(--secondary-color);
    color: white;
    border: none;

    &:hover {
      background-color: #1a1a1a;
    }
  }

  .btn-wishlist {
    border: 1px solid #d1d5db;
    background-color: white;
    color: #111827;

    &:hover {
      background-color: #f3f4f6;
    }
  }
}

.product-attributes {
  margin-bottom: 32px;

  .attribute-row {
    margin-bottom: 10px;
    font-size: 15px;
    line-height: 1.6;

    .meta-label {
      color: #6b7280;
      margin-right: 8px;
      font-weight: 600;
    }

    .attribute-value {
      color: #111827;
      text-transform: capitalize;
    }
  }
}

.categories-section {
  margin-bottom: 32px;

  .category-row,
  .tags-row {
    margin-bottom: 12px;
    font-size: 15px;

    .meta-label {
      color: #6b7280;
      margin-right: 8px;
    }

    a {
      color: #111827;
      text-decoration: none;

      &:hover {
        color: #e11d48;
      }
    }
  }
}

.social-share {
  display: flex;
  gap: 12px;

  .social-icon {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    text-decoration: none;
    transition: transform 0.2s;

    &:hover {
      transform: translateY(-2px);
    }

    &.facebook {
      background-color: #1877f2;
    }
    &.twitter {
      background-color: #1da1f2;
    }
    &.linkedin {
      background-color: #0a66c2;
    }
    &.pinterest {
      background-color: #e60023;
    }
    &.custom {
      background-color: var(--secondary-color);
    }

    i {
      font-size: 18px;
    }
  }
}

.navigation-controls {
  display: flex;
  justify-content: space-between;
  margin-top: 48px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;

  .nav-link {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #6b7280;
    text-decoration: none;
    font-size: 14px;
    font-weight: 500;

    &:hover {
      color: #e11d48;
    }
  }
}

.product-image-container {
  border-radius: 8px;
  overflow: hidden;
  // box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
  //   0 2px 4px -1px rgba(0, 0, 0, 0.06);

  .product-image {
    width: 100%;
    height: auto;
    object-fit: cover;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
      0 2px 4px -1px rgba(0, 0, 0, 0.06);
  }
}
.mini-view {
  width: 40px;
  height: auto;
  cursor: pointer;
  border-radius: 5px;
  &.active {
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
      0 2px 4px -1px rgba(0, 0, 0, 0.06);
  }
}

// Responsive adjustments
@media (max-width: 991.98px) {
  .product-title {
    font-size: 32px;
  }

  .product-meta {
    flex-wrap: wrap;
    gap: 16px;
  }

  .action-buttons {
    flex-direction: column;

    .btn {
      width: 100%;
    }
  }
}

/* Product Grid */
.product-tabs {
  border-bottom: none;
  margin-bottom: 30px;
}

.product-tabs .nav-link {
  color: var(--text-gray);
  border: none;
  padding: 10px 20px;
  font-weight: 500;
}

.product-tabs .nav-link.active {
  color: var(--primary-color);
  background: none;
  position: relative;
}

.product-tabs .nav-link.active::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 20px;
  right: 20px;
  height: 2px;
  background-color: var(--primary-color);
}

.product-card {
  background: white;
  border-radius: 8px;
  padding: 0 20px 0 0;
  text-align: center;
  transition: transform 0.3s;
  position: relative;
  /* box-shadow: 0 2px 10px rgba(0,0,0,0.05); */
}

.product-card::after {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  width: 1px;
  height: 100%;
  background-color: #ccc;
  opacity: 0.3;
}

.product-card:hover {
  transform: translateY(-5px);
}

.product-image {
  width: 100%;
  height: auto;
  border-radius: 15px;
  margin-bottom: 15px;
  cursor: pointer;
}

.product-title-mini {
  font-weight: 600;
  margin-bottom: 8px;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.product-brand {
  color: var(--text-gray);
  font-size: 14px;
  margin-bottom: 8px;
}

.product-rating {
  color: #ffc107;
  margin-bottom: 8px;
}

.product-price {
  color: var(--primary-color);
  font-weight: 700;
  font-size: 18px;
}

.custom-btn {
  background: var(--secondary-color);
  color: #fff;
  font-size: 14px;
  font-weight: 400;
  padding: 5px 15px;
  border-radius: 30px;
  &.outline {
    background: #fff;
    border: 1px solid var(--secondary-color);
    color: var(--text-gray);
  }
}

.counter-wrapper {
  max-width: 150px;
  display: flex;
  align-items: center;
  justify-content: space-around;
  gap: 10px;
  margin: 20px 0;
  padding: 10px;
  border: 1px solid var(--primary-color);
  border-radius: 50px;
  .btn-counter {
    font-size: 20px;
    line-height: 24px;
    background: transparent;
    border: none;
  }
}
</style>
